// Blocs d'une feuille (prototype lots F4 / F5, lot V5) : un tableau (▦) ou un arbre pondéré (⑂) que l'élève pose
// et remplit case par case. Le noyau (kernel/tables.js, kernel/trees.js) interprète et juge chaque case ; ce module
// réunit les modèles d'une feuille, et écrit / relit la ligne ENREGISTRÉE d'un bloc fini, pour que l'app et le
// rejeu serveur (`replayPacksSheet`) jugent le même bloc avec le même moteur. Pur, aucun DOM.
//
// Une feuille peut avoir :
//  - un bloc PRINCIPAL : `model.table` (valeurs, proportionnalité, signes, variations, statistiques, double entrée,
//    ensembles) ou `model.tree` (arbre pondéré) — un exercice « bloc » ;
//  - des OUTILS d'appoint d'une feuille « lignes » : `model.blocks` = { tree: {…}, variations: {…}, signs: {…},
//    pascal: {…} } (le triangle de Pascal est un outil de consultation, jamais jugé).
import {
  tableModel,
  givenGrid,
  judgeGrid,
  gridKeys,
  gridHint,
  solutionGrid,
} from './kernel/tables.js';
import { treeModel, givenTree, judgeTree, treeKeys, treeHint } from './kernel/trees.js';

/** Signe d'une ligne enregistrée qui porte un bloc. */
export const BLOCK_MARKS = { grid: '▦', tree: '⑂' };

function modelOf(engine, type, spec, symbols) {
  return type === 'tree'
    ? treeModel(engine, { model: { tree: spec } })
    : tableModel(engine, { model: { table: spec, symbols } });
}

/**
 * Les modèles des blocs d'une feuille : { main: { type, M } | null, aux: { nom: { type, M, spec } } }. Un modèle que
 * le noyau ne sait pas lire est ignoré (la feuille se joue sans lui).
 */
export function blockModels(engine, model) {
  const out = { main: null, aux: {} };
  const m = model || {};
  try {
    if (m.tree) out.main = { type: 'tree', M: modelOf(engine, 'tree', m.tree) };
    else if (m.table) out.main = { type: 'grid', M: modelOf(engine, 'grid', m.table, m.symbols) };
  } catch {
    out.main = null;
  }
  for (const [name, spec] of Object.entries(m.blocks || {})) {
    if (!spec || typeof spec !== 'object') continue;
    const type = name === 'tree' ? 'tree' : 'grid';
    try {
      const M =
        type === 'tree'
          ? modelOf(engine, 'tree', spec)
          : modelOf(engine, 'grid', { kind: name, ...spec }, m.symbols);
      out.aux[name] = { type, M, spec };
    } catch {
      /* outil illisible : ignoré */
    }
  }
  return out;
}

/** Le bloc fourni par l'énoncé (mode « given »), ou null : l'élève le construit. */
export function givenBlock(type, M) {
  if (M.mode !== 'given' && !M.spec?.tool) return null;
  return type === 'tree' ? givenTree(M) : givenGrid(M);
}

/** Juge un bloc (vue « noyau » : cases { uid, text, kind, locked }) → { cells, notes, done, … }. */
export function judgeBlock(type, M, view) {
  return type === 'tree' ? judgeTree(M, view) : judgeGrid(M, view);
}

/** Touches d'une case : { words, choices, palette }. */
export function blockKeys(type, M, view, at) {
  return type === 'tree'
    ? treeKeys(M, at?.part ?? 'p', view)
    : gridKeys(M, view, at?.r ?? -1, at?.c ?? 1);
}

/** Coup de pouce sur un bloc (la méthode, jamais la valeur). */
export function blockHint(type, M, view, at) {
  return type === 'tree'
    ? treeHint(M, at?.part ?? 'none', at?.depth ?? 1)
    : gridHint(M, view ?? { rows: [] }, at?.r ?? -1, at?.c ?? 1);
}

/** Le tableau attendu (signes, variations), pour le Corrigé ; null sinon. */
export function blockSolution(type, M) {
  if (type !== 'grid') return null;
  try {
    return solutionGrid(M);
  } catch {
    return null;
  }
}

/** Nombre de cases justes d'un bloc jugé (un arbre : seulement ses probabilités, pas les événements). */
export function okCellCount(judged, type = 'grid') {
  return Object.entries(judged?.cells || {}).filter(
    ([id, v]) => v.status === 'ok' && (type !== 'tree' || !id.endsWith('L')),
  ).length;
}

/** Les branches fournies par l'énoncé (arbre « given ») : textes et cases verrouillées. */
export function givenBranches(tpl) {
  const conv = (n) => ({
    label: n.label?.text ?? '',
    p: n.p?.text ?? '',
    path: n.path ? (n.path.text ?? '') : '',
    hasPath: !!n.path,
    lockLabel: !!n.label?.locked,
    lockP: !!n.p?.locked,
    children: (n.children || []).map(conv),
  });
  return (tpl?.root?.children || []).map(conv);
}

/**
 * Écriture COMPACTE d'un arbre (une ligne enregistrée tient en 400 caractères) : chaque branche est
 * [événement, probabilité, branches filles, probabilité du chemin] ; les cases vides sont "". Les identifiants des
 * cases se déduisent de la position (« 0.1L » : événement de la 2e branche de la 1re ; « P » probabilité,
 * « H » chemin) : l'interface retrouve ainsi l'état de chaque case jugée.
 */
export function treeLineText(aux, branches) {
  const enc = (b) => {
    const kids = (b.children || []).map(enc);
    const path = String(b.path ?? '');
    const out = [String(b.label ?? ''), String(b.p ?? '')];
    if (kids.length || path) out.push(kids);
    if (path) out.push(path);
    return out;
  };
  return `${BLOCK_MARKS.tree}${JSON.stringify({ a: aux, n: (branches || []).map(enc) })}`;
}

/** Identifiant de case d'une branche (« 0.1 ») et de sa partie. */
export const treeCellId = (at, part) => `${at}${part === 'label' ? 'L' : part === 'p' ? 'P' : 'H'}`;

/** Les branches écrites d'une ligne d'arbre : [{ label, p, path, children }] (textes), ou null. */
export function treeBranchesOf(n) {
  if (!Array.isArray(n)) return null;
  const dec = (x) => {
    if (!Array.isArray(x) || typeof x[0] !== 'string' || typeof x[1] !== 'string')
      throw new Error('arbre');
    const kids = Array.isArray(x[2]) ? x[2].map(dec) : [];
    return { label: x[0], p: x[1], path: typeof x[3] === 'string' ? x[3] : '', children: kids };
  };
  try {
    return n.map(dec);
  } catch {
    return null;
  }
}

/** Vue « noyau » d'un arbre écrit : cases à identifiants de position. */
export function treeKernelView(branches) {
  const cell = (id, text, kind) => ({ uid: id, text, kind: text.trim() ? kind : '' });
  const node = (b, at) => ({
    uid: `${at}N`,
    label: cell(treeCellId(at, 'label'), b.label, 'word'),
    p: cell(treeCellId(at, 'p'), b.p, 'expr'),
    path: cell(treeCellId(at, 'path'), b.path ?? '', 'expr'),
    children: (b.children || []).map((c, i) => node(c, `${at}.${i}`)),
  });
  return { root: { uid: 'R', children: branches.map((b, i) => node(b, String(i))) } };
}

/** Ligne enregistrée d'un tableau d'appoint : « ▦{…} ». */
export function blockLineText(type, view, aux = null) {
  if (type === 'tree') throw new Error('un arbre s’écrit avec treeLineText');
  return `${BLOCK_MARKS.grid}${JSON.stringify({ aux, rows: view.rows, colKinds: view.colKinds ?? null })}`;
}

/** Relit une ligne enregistrée de bloc : { type, aux, view } ou null. */
export function parseBlockLine(text) {
  const t = String(text ?? '');
  const type = t.startsWith(BLOCK_MARKS.grid)
    ? 'grid'
    : t.startsWith(BLOCK_MARKS.tree)
      ? 'tree'
      : null;
  if (!type) return null;
  try {
    const body = JSON.parse(t.slice(1));
    if (!body || typeof body !== 'object') return null;
    if (type === 'tree') {
      const branches = treeBranchesOf(body.n);
      if (!branches) return null;
      return {
        type,
        aux: typeof body.a === 'string' ? body.a : null,
        view: treeKernelView(branches),
        branches,
      };
    }
    if (!Array.isArray(body.rows)) return null;
    const view = { rows: body.rows, ...(body.colKinds ? { colKinds: body.colKinds } : {}) };
    return { type, aux: typeof body.aux === 'string' ? body.aux : null, view };
  } catch {
    return null;
  }
}
